/* Built In Imports */
import NextLink from 'next/link';
import { useRouter } from 'next/router';

/* External Imports */
import { Box, Flex, Heading, Icon, Image, Link, Text } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { FaRegCheckCircle } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';
import { EnrollSectionSt } from '@ieo/Common/CompLangConfig';
import RegisterForm from '@ieo/Components/Form/RegisterForm';
import {
  EnrollContextProvider,
  useEnrollContext,
} from '@ieo/Components/Form/ieo/context/enrollContext';
import loadingIcon from '@ieo/Components/ThankYou/assets/loadingIcon.svg';
import { usePageState } from '@ieo/Components/ThankYou/usePageState';
import YouthOfferMsgs from '@ieo/Components/YouthOfferMsgs';

/* Services */
import { searchByFieldId } from '@components/Utility/Shared/SharedService';
import { usePageConfigValue } from '@ieo/hooks/usePageConfigVal';

/**
 *
 * @param {Object} SectionContent
 * @param {String} lang
 * @param  children
 * @returns {ReactElement} EnrollSectionUi
 */

export const EnrollSectionUi = ({
  children,
  lang,
  sectionContent,
  region,
  pageData,
  maxW,
  context,
  pageName,
  prgData,
}) => {
  const isLessThen = useMediaQuery({ maxWidth: 991 });
  const router = useRouter();
  const { type } = router.query;

  const pageState = usePageState();
  const checkStatusOfPayment = router.query?.status;

  //console.log('usePageState', pageState.status);

  // const changeContentblock = router.pathname.includes('inner-engineering/gift');
  const isArmyOfferURL = router.asPath.includes('armed-forces');
  const isThankYouURL =
    router.asPath.includes('register/thankyou') &&
    !router.pathname.includes('inner-engineering/gift');
  const isTransfer = type === 'transfer';
  let isFormVisible = true;
  let showInProgressScreen = false;
  const { contextData, setContextData } = useEnrollContext();
  const thanksGiftImg = sectionContent?.find(e => e.fieldId === 'thanks-gift');
  const thanksGivingMessage = sectionContent?.find(
    e => e.fieldId === 'thanksgiving-message'
  );
  const currentCoupon = prgData && Object.values(prgData)[0]?.coupon;

  const { pageConfigVal, isThemeActive } = usePageConfigValue(
    pageData,
    region,
    currentCoupon
  );
  // condition to check is form will be visible for youth offer or not

  if (pageName === 'yo') {
    if (
      region === 'in' &&
      (!context || context?.age < 15 || context?.age > 24)
    ) {
      isFormVisible = false;
    }
  }

  // army offer validation check
  if (contextData?.validationFailed) {
    isFormVisible = false;
  }

  if (
    contextData?.loading &&
    contextData?.step === 2 &&
    contextData?.showProgressScreen
  ) {
    // isFormVisible = false;
    showInProgressScreen = true;
  }

  const isLatamCoutries = () => {
    return (
      pageData?.country &&
      config?.latinAmericaCountries?.indexOf(pageData?.country) > -1
    );
  };

  const ThankYouMessageHeading = () => {
    const getContent = () => {
      if (isThankYouURL) {
        if (pageState.status === 'done') {
          return sectionContent?.[0];
        } else if (
          pageState.messageStatus === 'error' ||
          pageState.messageStatus === 'inprogress' ||
          pageState.messageStatus === 'InProgress'
        ) {
          return sectionContent?.[2];
        }
      }
      return sectionContent?.[0];
    };

    const content = getContent();
    const titleSubtext = isTransfer
      ? sectionContent?.[sectionContent.length - 1]?.titleSubtext ||
        content?.titleSubtext ||
        'Continue to the'
      : content?.titleSubtext;

    return (
      <>
        <Text
          lineHeight={{ base: '22px', md: '35px' }}
          {...EnrollSectionSt[lang]?.subTitle}
          px={3}
          fontSize={{ base: '18px', md: '32px' }}
          fontFamily={{
            base: isThankYouURL ? 'FedraSansStd-medium' : 'FedraSansStd-book',
            md: 'FedraSansStd-book',
          }}
        >
          {titleSubtext}
        </Text>
        <Heading
          as="h2"
          {...EnrollSectionSt[lang]?.h2}
          textTransform={{ base: 'uppercase', md: 'none' }}
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
          px={3}
          fontSize={{ base: '18px', md: '44px', lg: '64px' }}
          fontFamily={'FedraSansStd-medium'}
        >
          {content?.titleText}
        </Heading>
        <OfferText display={{ base: 'none', md: 'flex' }} pageData={pageData} />
      </>
    );
  };

  const OfferText = ({ ...props }) => {
    return (
      <>
        {(isLatamCoutries() ||
          pageData?.country === 'US' ||
          pageData?.country === 'CA') &&
          isThemeActive &&
          thanksGivingMessage &&
          render(thanksGivingMessage?.body) !== '<p></p>' &&
          render(thanksGivingMessage?.body) !== '<p><br></p>' &&
          prgData && (
            <Box
              justifyContent="flex-start"
              alignItems="center"
              gap="10px"
              pt="10px"
              // display={display}
              {...props}
            >
              <Box
                borderTop={{
                  base: '1px solid #F48221',
                  md: '2px solid #F48221',
                }}
                w="full"
                mt="20px"
              >
                <Box display="flex" gap="10px" pt="10px" alignItems="center">
                  <LazyLoadImageComponent
                    src={thanksGiftImg?.image?.url}
                    alt="gift"
                    maxH="61px"
                    maxW={{ md: '64px' }}
                    objectFit="cover"
                    w={{ base: '72px', md: '64px' }}
                  />
                  <StructuredTextParser
                    str={render(thanksGivingMessage?.body)
                      ?.replace(
                        '##COUPONCODE##',
                        Object.values(prgData)[0]?.coupon?.couponCode
                      )
                      .replace('##CURRENCY##', Object.values(prgData)[0]?.curr)
                      .replace(
                        '##PRICE##',
                        Object.values(prgData)[0]?.coupon?.offerValue
                      )}
                    color="#fff"
                    fontWeight="400"
                    fontStyle="500"
                    mb={{ base: '8px', md: '0' }}
                    ml={{ base: '0', md: '0', lg: '0' }}
                    fontFamily="'FedraSansStd-book'"
                    fontSize="18px"
                    // w="70%"
                  />
                </Box>
              </Box>
            </Box>
          )}
      </>
    );
  };

  return (
    <Box
      height="auto"
      pb={
        region === 'us' || region === 'ca'
          ? '160px'
          : lang === 'es'
          ? '22rem'
          : '100px'
      }
      mb={pageName === 'corporate-register' && '10rem'}
    >
      <Box
        background={{
          base:
            isThemeActive && pageConfigVal?.enrollProgram?.bgImage?.mob
              ? `url(${pageConfigVal?.enrollProgram?.bgImage?.mob})  #00005B`
              : `url(${config.imgPath}/d/46272/1735020980-group-1171277574.jpg) #00005B`,

          md:
            isThemeActive && pageConfigVal?.enrollProgram?.bgImage?.tab
              ? `url(${pageConfigVal?.enrollProgram?.bgImage?.tab})  #00005B`
              : `url(${config.imgPath}/d/46272/1735020980-group-1171277574.jpg) #00005B`,
          lg:
            isThemeActive && pageConfigVal?.enrollProgram?.bgImage?.desktop
              ? `url(${pageConfigVal?.enrollProgram?.bgImage?.desktop})  #00005B`
              : ` url(${config.imgPath}/d/46272/1735020980-group-1171277573.jpg) #00005B`,
          xl:
            isThemeActive && pageConfigVal?.enrollProgram?.bgImage?.bigScreen
              ? `url(${pageConfigVal?.enrollProgram?.bgImage?.bigScreen})`
              : `url(${config.imgPath}/d/46272/1735020980-group-1171277573.jpg) #00005B`,
        }}
        pb={region === 'us' || region === 'ca' ? '50px' : '150px'}
        bgRepeat={'no-repeat'}
        height="auto"
        bgSize={{ base: 'cover !important', lg: 'auto' }}
        minH={{
          base: !isFormVisible ? 0 : '700px',
          md: !isFormVisible ? 0 : '780px',
          lg: !isFormVisible
            ? 0
            : region === 'us' || region === 'ca'
            ? '600px'
            : '500px',
          xl: !isFormVisible
            ? 0
            : region === 'us' || region === 'ca'
            ? '500px'
            : '600px',
        }}
        bgColor={isThemeActive ? '#1B1E78 !important' : '#00005B !important'}
      >
        {/*Start Error UI*/}
        {isArmyOfferURL && contextData?.validationFailed && (
          <Flex
            maxW={1030}
            bgRepeat="no-repeat"
            mx={{ base: '15px', sm: '30px', md: '50px', xl: 'auto' }}
            bg="#fff"
            p={{
              base: '15px',
              md: '30px',
              lg: '40px',
            }}
            boxSizing="border-box"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gridGap="10px"
            my={{ base: '40px', md: '60px' }}
          >
            <Box
              display="flex"
              gap="5px"
              textAlign={{ base: 'left', md: 'center' }}
              alignItems={{ base: 'flex-start', md: 'center' }}
              maxW={{ base: '290px', sm: '400px', md: '100%' }}
              w="full"
              justifyContent="center"
            >
              <FaRegCheckCircle color="#D30000" fontSize="30px" />
              <StructuredTextParser
                mt="0px"
                str={render(
                  searchByFieldId(sectionContent, 'yo-failed-head')?.body
                )}
                color="#D30000"
                fontSize={{ base: '20px', md: '24px' }}
                fontFamily="FedraSansStd-medium"
              />
            </Box>
            <Box
              maxW={{ base: '290px', sm: '400px', md: '100%' }}
              w="full"
              textAlign="center"
            >
              {searchByFieldId(sectionContent, 'army-failed-text-2') && (
                <StructuredTextParser
                  mt={{ base: '10px', lg: '30px' }}
                  str={render(
                    searchByFieldId(sectionContent, 'army-failed-text-2')?.body
                  )}
                  color="#433F3C"
                  fontSize={{ base: '16px', md: '18px' }}
                  fontFamily="FedraSansStd-medium"
                  textAlign="center"
                  mb="20px"
                />
              )}
              {searchByFieldId(sectionContent, 'army-failed-reenter-id') && (
                <Link
                  onClick={() => {
                    setContextData({ validationFailed: false });
                  }}
                  color="#F37021"
                  textDecoration="underline"
                  paddingTop="10px"
                >
                  {
                    searchByFieldId(sectionContent, 'army-failed-reenter-id')
                      ?.buttonText
                  }
                </Link>
              )}
            </Box>
            <Box
              mx="auto"
              w="90px"
              bg="#fff"
              fontFamily="FedraSansStd-medium"
              p={{ base: '10px', md: '20px' }}
              position="relative"
              textAlign="center"
              mt={{ base: '10px', lg: '20px' }}
            >
              OR
            </Box>
            <Box
              borderBottom="1px solid #5B5237"
              w={{ base: '100%', sm: '60%' }}
              mt={{ base: '-30px', lg: '-40px' }}
              mb={{ base: '10px', lg: '40px' }}
            ></Box>
            <StructuredTextParser
              str={render(
                searchByFieldId(sectionContent, 'yo-failed-text1')?.body
              )}
              color="#433F3C"
              fontSize={{ base: '16px', md: '18px' }}
              fontFamily="FedraSansStd-book"
              textAlign="center"
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              className="aLink"
              mt="10px"
            />
            <Box
              mx="auto"
              w="90px"
              bg="#fff"
              fontFamily="FedraSansStd-medium"
              p={{ base: '10px', md: '20px' }}
              position="relative"
              textAlign="center"
              mt={{ base: '10px', lg: '20px' }}
            >
              OR{' '}
            </Box>
            <Box
              borderBottom="1px solid #5B5237"
              w={{ base: '100%', sm: '60%' }}
              mt={{ base: '-30px', lg: '-40px' }}
              mb={{ base: '20px', lg: '40px' }}
            ></Box>
            <StructuredTextParser
              str={render(
                searchByFieldId(sectionContent, 'yo-failed-text3')?.body
              )}
              color="#433F3C"
              fontSize={{ base: '16px', md: '18px' }}
              fontFamily="FedraSansStd-book"
              textAlign="center"
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              className="aLink"
              mt="0px"
              maxW="auto"
            />
            <StructuredTextParser
              str={render(
                searchByFieldId(sectionContent, 'yo-failed-text4')?.body
              )}
              color="#433F3C"
              fontSize={{ base: '16px', md: '18px' }}
              fontFamily="FedraSansStd-book"
              textAlign="center"
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              className="aLink"
              mt="20px"
              maxW="auto"
            />
            {sectionContent[16]?.buttonText && (
              <Text py={{ base: '15px', md: '35px' }}>
                <Link
                  fontFamily="FedraSansStd-book"
                  fontSize={{ base: '18px', lg: '22px' }}
                  color="#F37021"
                  textDecor="underline"
                  _hover={{ textDecor: 'none' }}
                  href={refineUrlDomain(
                    sectionContent[16]?.linkUrl,
                    region,
                    lang
                  )}
                >
                  {' '}
                  {sectionContent[16]?.buttonText}{' '}
                </Link>
              </Text>
            )}
            <style>
              {`
                .aLink p a {
                  color:#F37021;
                }
                .textPrimaryColor p:last-child{
                  color:#F37021;
                  text-decoration: underline;
                  padding-top:10px
                }

                `}
            </style>
          </Flex>
        )}
        {/*End Error UI*/}

        {!isArmyOfferURL && (
          <YouthOfferMsgs
            sectionContent={sectionContent}
            context={context}
            region={region}
            lang={lang}
          />
        )}
        {isFormVisible && (
          <Flex
            id="ieo-enroll-section"
            pt={{ base: '0px', md: '40px' }}
            pb={{ base: '0px', md: '0px' }}
            pos="relative"
            zIndex="8"
            fontFamily={'FedraSansStd-medium'}
            margin={'0 auto'}
          >
            <Flex
              maxW={maxW || 1030}
              w="full"
              mx="auto"
              flexWrap="wrap"
              px={{ base: '0px', xl: 0 }}
              mb={{
                base:
                  region === 'us' || region === 'ca'
                    ? '-250px'
                    : '-150px' || lang === 'es'
                    ? '-170px'
                    : '-150px',
                md:
                  region === 'us' || region === 'ca'
                    ? '-250px'
                    : '-150px' || lang === 'es'
                    ? '-250px'
                    : '-150px',
              }}
            >
              <Flex
                w="full"
                flexDir={{ base: 'column', lg: 'row' }}
                justifyContent="space-between"
                gridGap={{
                  base: '0px',
                  lg: '100px',
                  xl: '60px',
                }}
                css={{
                  '-webkit-grid-gap': '40px',
                  '@media screen and (min-width: 1024px)': {
                    '-webkit-grid-gap': '100px',
                  },
                }}
                // pb="40px"
              >
                <Box
                  color="#fff"
                  w={{ base: '100%', md: '100%', lg: '50%' }}
                  pt={{
                    base: isThankYouURL ? '5px' : '10px',
                    lg:
                      region === 'us' ? '45px' : isThankYouURL ? '0px' : '40px',
                    xl:
                      region === 'us' ? '45px' : isThankYouURL ? '0px' : '5px',
                  }}
                  pb={{ base: isThankYouURL && '5px', md: 'inherit' }}
                  justifyContent={
                    lang === 'ar' && isThankYouURL
                      ? 'space-between'
                      : lang === 'ar'
                      ? 'flex-end'
                      : 'space-between'
                  }
                  alignItems="center"
                  display={{ base: 'flex', lg: 'block' }}
                  flexWrap="wrap"
                  mt={{ base: '0px' }}
                  bgColor={{
                    base: isThankYouURL ? '#00A171' : 'transparent',
                    md: 'transparent',
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent={{
                      base: 'center',
                      md: 'flex-star',
                      lg: 'flex-start',
                    }}
                    alignItems={{
                      base: isThankYouURL ? 'center' : 'flex-start',
                      lg: 'flex-start',
                    }}
                    textAlign={{ base: 'center', lg: 'left' }}
                    width="100%"
                    flexDir="column"
                    p={{ base: '10px 0', md: '0' }}
                  >
                    {isThankYouURL && (
                      <>
                        {/* Mobile View */}
                        <Box display={{ base: 'block', md: 'none' }} px={3}>
                          <Icon width="9" height="9" viewBox="0 0 32 32">
                            <svg fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M13.9998 27.3346C21.3636 27.3346 27.3332 21.3651 27.3332 14.0013C27.3332 6.63751 21.3636 0.667969 13.9998 0.667969C6.63604 0.667969 0.666504 6.63751 0.666504 14.0013C0.666504 21.3651 6.63604 27.3346 13.9998 27.3346ZM12.099 19.9859L19.8763 10.4803L17.8124 8.79167L11.9011 16.0167L9.33337 13.449L7.44775 15.3346L12.099 19.9859Z"
                                fill="#F8F4EB"
                              />
                            </svg>
                          </Icon>
                          <Box display="flex" alignItems="baseline" gap={1}>
                            <Text
                              pt="15px"
                              lineHeight="normal"
                              {...EnrollSectionSt[lang]?.subTitle}
                              fontSize="14px"
                              fontFamily="'FedraSansStd-medium'"
                              textAlign={'center'}
                            >
                              {(() => {
                                const isPaymentInProgressOrError =
                                  checkStatusOfPayment?.toLowerCase() ===
                                    'inprogress' ||
                                  checkStatusOfPayment?.toLowerCase() ===
                                    'InProgress' ||
                                  checkStatusOfPayment?.toLowerCase() ===
                                    'error';

                                return isTransfer
                                  ? sectionContent?.[sectionContent.length - 1]
                                      ?.titleSubtext ||
                                      sectionContent?.[2]?.titleSubtext ||
                                      'Continue to the'
                                  : isPaymentInProgressOrError
                                  ? sectionContent?.[2]?.titleSubtext
                                  : isLessThen
                                  ? sectionContent?.[3]?.titleSubtext
                                  : sectionContent?.[0]?.titleSubtext;
                              })()}
                            </Text>

                            <Heading
                              as="h2"
                              {...EnrollSectionSt[lang]?.h2}
                              dir={lang === 'ar' ? 'rtl' : 'ltr'}
                              fontSize="14px"
                              fontFamily="FedraSansStd-medium"
                              lineHeight="normal"
                              textAlign="center"
                              textTransform="uppercase"
                            >
                              {(() => {
                                const isPaymentInProgressOrError =
                                  pageState.messageStatus === 'inprogress' ||
                                  pageState.messageStatus === 'InProgress' ||
                                  pageState.messageStatus === 'error';

                                return isPaymentInProgressOrError
                                  ? sectionContent?.[2]?.titleText
                                  : isLessThen
                                  ? sectionContent?.[3]?.titleText
                                  : sectionContent?.[0]?.titleText;
                              })()}{' '}
                            </Heading>
                          </Box>
                        </Box>

                        {/* Desktop View */}
                        <Box display={{ base: 'none', md: 'block' }} px={5}>
                          <ThankYouMessageHeading />
                        </Box>
                      </>
                    )}

                    {!isThankYouURL && (
                      <Box
                        display="flex"
                        width="100%"
                        justifyContent="space-between"
                        p="8px 0"
                        alignItems="flex-end"
                      >
                        <Box
                          display="flex"
                          alignItems="flex-start"
                          flexDir="column"
                        >
                          <ThankYouMessageHeading />
                        </Box>

                        <Box display={{ base: 'flex', lg: 'none' }}>
                          <LazyLoadImageComponent
                            src={
                              isLessThen
                                ? sectionContent?.[1]?.mobileImage?.url
                                  ? sectionContent?.[1]?.mobileImage?.url
                                  : sectionContent?.[1]?.image?.url
                                : sectionContent?.[1]?.image?.url
                            }
                            alt={sectionContent?.[1]?.image?.alt || ''}
                            height={{
                              base: '100px',
                              md: isLessThen ? '240px' : 'auto',
                            }}
                            position="relative"
                            top={isLessThen ? '0px' : '60px'}
                            pt={{
                              base: isThankYouURL ? '0px' : '5px',
                              lg:
                                region === 'us'
                                  ? '40px'
                                  : isThankYouURL
                                  ? '100px'
                                  : '10px',
                            }}
                            pr={{
                              base: (isThankYouURL && '15px') || '',
                              md: '0px',
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    {/* Holiday offer (Thanks code) removed from US and CA */}
                    {/* {(isUS || isCA) && (
                  <Box
                    display={{ base: "none", md: "flex" }}
                    gridGap={'0px'}
                    width="272px"
                    borderTop="1px solid #F37021"
                    mt="20px"
                    pt="10px"
                    alignItems="center"
                    justifyContent="center">
                    <Image
                      src={changeContentblock ? sectionContent[8]?.image?.url : sectionContent[15]?.image?.url}
                      width={'56px'}
                      height={'56px'}
                      mt={'5px'}
                      mr={'5px'}
                    />
                    <Box color={'#fff'} width={'210px'}>
                      <StructuredTextParser
                        str={changeContentblock ? render(sectionContent[9]?.body) : render(sectionContent[16]?.body)}
                        region={region}
                        lang={lang}
                        className="thanksmessage"
                      />
                    </Box>
                  </Box>
                )} */}
                    {/* Holiday offer (Thanks code) removed from US and CA */}
                  </Box>
                  {searchByFieldId(sectionContent, 'ieo-student-offer')?.body
                    ?.value && (
                    <Box
                      p={{ base: '30px 0' }}
                      display={{ base: 'flex', md: 'block' }}
                      w="100%"
                    >
                      <Box
                        width="100%"
                        maxW="1030px"
                        bg="#fff"
                        mx="auto"
                        display="flex"
                        gap="10px"
                        justifyContent="center"
                        alignItems="center"
                        p="20px"
                      >
                        <Box>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20.7489 12.0002L21.8529 10.0922C21.9856 9.86262 22.0217 9.58977 21.9532 9.33361C21.8848 9.07745 21.7174 8.85896 21.4879 8.72618L19.5779 7.62218V5.42218C19.5779 5.15696 19.4725 4.90261 19.285 4.71507C19.0975 4.52754 18.8431 4.42218 18.5779 4.42218H16.3789L15.2759 2.51318C15.1427 2.2841 14.9246 2.11668 14.6689 2.04718C14.5421 2.0128 14.4097 2.00393 14.2794 2.0211C14.1491 2.03826 14.0235 2.08112 13.9099 2.14718L11.9999 3.25118L10.0899 2.14618C9.86021 2.01358 9.58726 1.97764 9.33109 2.04628C9.07492 2.11492 8.8565 2.28251 8.72389 2.51218L7.61989 4.42218H5.42089C5.15567 4.42218 4.90132 4.52754 4.71378 4.71507C4.52625 4.90261 4.42089 5.15696 4.42089 5.42218V7.62118L2.51089 8.72518C2.39692 8.79074 2.29703 8.87816 2.21695 8.98244C2.13688 9.08672 2.07819 9.20579 2.04428 9.33282C2.01036 9.45985 2.00188 9.59232 2.01931 9.72264C2.03675 9.85295 2.07977 9.97854 2.14589 10.0922L3.24989 12.0002L2.14589 13.9082C2.01388 14.138 1.97806 14.4106 2.04625 14.6667C2.11443 14.9227 2.28109 15.1415 2.50989 15.2752L4.41989 16.3792V18.5782C4.41989 18.8434 4.52525 19.0977 4.71278 19.2853C4.90032 19.4728 5.15467 19.5782 5.41989 19.5782H7.61989L8.72389 21.4882C8.81242 21.6395 8.93882 21.7652 9.09067 21.8529C9.24251 21.9405 9.41456 21.9872 9.58989 21.9882C9.76389 21.9882 9.93689 21.9422 10.0909 21.8532L11.9989 20.7492L13.9089 21.8532C14.1385 21.9856 14.4113 22.0215 14.6674 21.9531C14.9234 21.8847 15.1419 21.7175 15.2749 21.4882L16.3779 19.5782H18.5769C18.8421 19.5782 19.0965 19.4728 19.284 19.2853C19.4715 19.0977 19.5769 18.8434 19.5769 18.5782V16.3792L21.4869 15.2752C21.6007 15.2094 21.7004 15.1219 21.7803 15.0176C21.8602 14.9133 21.9188 14.7943 21.9527 14.6673C21.9866 14.5404 21.9951 14.408 21.9778 14.2777C21.9605 14.1475 21.9177 14.0219 21.8519 13.9082L20.7489 12.0002ZM9.49889 6.99018C9.89685 6.99031 10.2785 7.14853 10.5598 7.43002C10.8411 7.71151 10.999 8.09322 10.9989 8.49118C10.9988 8.88914 10.8405 9.27074 10.559 9.55205C10.2776 9.83335 9.89585 9.99131 9.49789 9.99118C9.09993 9.99105 8.71833 9.83283 8.43702 9.55134C8.15572 9.26985 7.99776 8.88814 7.99789 8.49018C7.99802 8.09222 8.15624 7.71062 8.43773 7.42931C8.71922 7.14801 9.10093 6.99005 9.49889 6.99018ZM9.79889 16.5902L8.19889 15.3912L14.1989 7.39118L15.7989 8.59018L9.79889 16.5902ZM14.4989 16.9902C14.3018 16.9901 14.1067 16.9512 13.9247 16.8758C13.7427 16.8003 13.5773 16.6897 13.438 16.5503C13.2987 16.411 13.1883 16.2455 13.1129 16.0634C13.0376 15.8814 12.9988 15.6862 12.9989 15.4892C12.999 15.2921 13.0378 15.097 13.1133 14.915C13.1888 14.733 13.2993 14.5676 13.4387 14.4283C13.5781 14.289 13.7436 14.1786 13.9256 14.1032C14.1077 14.0279 14.3028 13.9891 14.4999 13.9892C14.8978 13.9893 15.2795 14.1475 15.5608 14.429C15.8421 14.7105 16 15.0922 15.9999 15.4902C15.9998 15.8881 15.8415 16.2697 15.56 16.551C15.2786 16.8324 14.8968 16.9903 14.4989 16.9902Z"
                              fill="#BE1931"
                            />
                          </svg>
                        </Box>
                        <Box fontFamily="FedraSansStd-medium">
                          <StructuredTextParser
                            str={render(
                              searchByFieldId(
                                sectionContent,
                                'ieo-student-offer'
                              ).body.value
                            )}
                            fontSize="16px"
                            color="#6D6358"
                            display="flex"
                            justifyContent="center"
                            fontFamily="'FedraSansStd-medium'"
                            className="st-offer"
                            mt="0"
                          />
                          <style>
                            {`.st-offer a {
                                  text-decoration: underline;
                                }`}
                          </style>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {isThankYouURL && pageState.status === 'done' ? (
                    <Box display={{ base: 'none', lg: 'flex' }}>
                      <LazyLoadImageComponent
                        src={
                          isLessThen
                            ? sectionContent?.[1]?.mobileImage?.url != null
                              ? sectionContent?.[1]?.image?.url
                              : sectionContent?.[1]?.image?.url
                            : sectionContent?.[1]?.image?.url
                        }
                        alt={sectionContent?.[1]?.image?.alt || ''}
                        height={{
                          base: '140px',
                          md: isLessThen ? '240px' : 'auto',
                        }}
                        position="relative"
                        top={isLessThen ? '0px' : '60px'}
                        pt={{
                          base: isThankYouURL ? '0px' : '5px',
                          lg:
                            region === 'us'
                              ? '40px'
                              : isThankYouURL
                              ? '100px'
                              : '10px',
                        }}
                        pr={{
                          base: (isThankYouURL && '15px') || '',
                          md: '0px',
                        }}
                      />
                    </Box>
                  ) : pageState.status !== 'done' ? (
                    <Box display={{ base: 'none', lg: 'flex' }}>
                      <LazyLoadImageComponent
                        src={
                          isLessThen
                            ? sectionContent?.[1]?.mobileImage?.url
                              ? sectionContent?.[1]?.mobileImage?.url
                              : sectionContent?.[1]?.image?.url
                            : sectionContent?.[1]?.image?.url
                        }
                        alt={sectionContent?.[1]?.image?.alt || ''}
                        height={{
                          base: '140px',
                          md: isLessThen ? '240px' : 'auto',
                        }}
                        position="relative"
                        top={isLessThen ? '0px' : '60px'}
                        pt={{
                          base: isThankYouURL ? '0px' : '5px',
                          lg:
                            region === 'us'
                              ? '40px'
                              : isThankYouURL
                              ? '100px'
                              : '10px',
                        }}
                        pr={{
                          base: (isThankYouURL && '15px') || '',
                          md: '0px',
                        }}
                      />
                    </Box>
                  ) : pageState.messageStatus === 'InProgress' ||
                    pageState.messageStatus === 'inprogress' ||
                    pageState.status === 'loading' ? (
                    <LazyLoadImageComponent
                      src={
                        'https://static.sadhguru.org/d/46272/1737446815-ie_remarketing_landing_page_illustration_bha_2-removebg-preview-1.png'
                      }
                      alt={sectionContent?.[1]?.image?.alt || ''}
                      height={{
                        base: '140px',
                        md: isLessThen ? '240px' : 'auto',
                      }}
                      position="relative"
                      top={isLessThen ? '0px' : '60px'}
                      pt={{
                        base: isThankYouURL ? '0px' : '5px',
                        lg:
                          region === 'us'
                            ? '40px'
                            : isThankYouURL
                            ? '100px'
                            : '10px',
                      }}
                      pr={{ base: (isThankYouURL && '15px') || '', md: '0px' }}
                    />
                  ) : (
                    pageState.messageStatus === 'error' && (
                      <LazyLoadImageComponent
                        src={
                          'https://static.sadhguru.org/d/46272/1737446811-ie_remarketing_landing_page_illustration_bha_5-removebg-preview-1.png'
                        }
                        alt={sectionContent?.[1]?.image?.alt || ''}
                        height={{
                          base: '140px',
                          md: isLessThen ? '240px' : 'auto',
                        }}
                        position="relative"
                        top={isLessThen ? '0px' : '60px'}
                        pt={{
                          base: isThankYouURL ? '0px' : '5px',
                          lg:
                            region === 'us'
                              ? '40px'
                              : isThankYouURL
                              ? '100px'
                              : '10px',
                        }}
                        pr={{
                          base: (isThankYouURL && '15px') || '',
                          md: '0px',
                        }}
                      />
                    )
                  )}

                  <OfferText
                    display={{ base: 'flex', md: 'none' }}
                    width="100%"
                  />
                  {/* Holiday offer (Thanks code) removed from US and CA */}
                  {/* {(isUS || isCA) && (
                <Box width="100%"
                  display={{ base: "flex", md: "none" }} justifyContent="center">
                  <Box
                    display={{ base: "flex", md: "none" }}
                    gridGap={'0px'}
                    width="292px"
                    borderTop="1px solid #F37021"
                    mt="20px"
                    pt="10px"
                    alignItems="center"
                    justifyContent="center">
                    <Image
                      src={changeContentblock ? sectionContent[8]?.image?.url : sectionContent[15]?.image?.url}
                      width={'56px'}
                      height={'56px'}
                      mt={'5px'}
                      mr={'5px'}
                    />
                    <Box color={'#fff'} width={'210px'}>
                      <StructuredTextParser
                        str={changeContentblock ? render(sectionContent[9]?.body) : render(sectionContent[16]?.body)}
                        region={region}
                        lang={lang}
                        className="thanksmessage"
                      />
                    </Box>
                  </Box>
                </Box>
              )} */}
                  {/* <StructuredTextParser
                mt="10px"
                str={render(sectionContent?.[1].body)}
                //{...ProgramFeeSt[lang].p}
                maxW="auto"
              /> */}
                  {/* Holiday offer (Thanks code) removed from US and CA */}
                </Box>
                <Flex
                  flexGrow="1"
                  flexDir="column"
                  maxW="700px"
                  w="full"
                  justifyContent="center"
                  alignItems={lang === 'es' && 'center'}
                  position={lang === 'es' && 'relative'}
                  px="0"
                >
                  <Flex
                    bg="#fff"
                    flexGrow="1"
                    flexDir="column"
                    flex="none"
                    boxShadow={{
                      base: isThankYouURL
                        ? 'none'
                        : '0 12px 22px -15px #6D6358',
                      md: '0 12px 22px -15px #6D6358',
                    }}
                    maxW="700px"
                    mt={{
                      base: '0px',
                      md: (region === 'us' || region === 'ca') && '45px',
                    }}
                    w="full"
                  >
                    <Box display={showInProgressScreen ? 'none' : 'block'}>
                      {children}
                    </Box>
                    <Box
                      textAlign="center"
                      minH={{ base: '350px', lg: '500px' }}
                      p={{ base: '80px 0 120px 0', lg: '150px 0 200px 0' }}
                      display={showInProgressScreen ? 'block' : 'none'}
                    >
                      <Image
                        src="https://static.sadhguru.org/d/46272/1690792477-isha_logo_black.svg"
                        alt="Isha Logo"
                        width="75px"
                        pb={{ base: '20px', lg: '80px' }}
                      />
                      <Box>
                        <Image
                          src={loadingIcon.src}
                          width="50px"
                          alt="Loading..."
                          animation="infiniteRotate 2s linear infinite"
                        />
                        <Text
                          color="#6D6358"
                          fontSize={{ base: '18px', md: '22px' }}
                          py="15px"
                          m="0 auto"
                          // maxW="400px"
                        >
                          Secure payment gateway is opening. <br />
                          Do not refresh or press the back button.
                        </Text>
                      </Box>
                      <style jsx>
                        {`
                          @keyframes infiniteRotate {
                            0% {
                              transform: rotate(0deg);
                            }
                            100% {
                              transform: rotate(360deg);
                            }
                          }
                        `}
                      </style>
                    </Box>
                  </Flex>

                  <Box
                    lineHeight="126%"
                    mt={{ base: '20px', md: '40px', lg: '20px' }}
                    px={{ base: '0', md: '13%' }}
                    position={lang === 'es' && 'absolute'}
                    bottom={{
                      base: lang === 'es' && '-9rem',
                      md: lang === 'es' && '-12rem',
                      lg: lang === 'es' && isLessThen ? '-10rem' : '-8rem',
                    }}
                  >
                    {(region === 'us' ||
                      region === 'ca' ||
                      pageData?.country === 'US' ||
                      pageData?.country === 'CA' ||
                      isLatamCoutries()) &&
                      sectionContent[18]?.linkUrl &&
                      sectionContent[18]?._modelApiKey === 'simple_button' && (
                        <>
                          <StructuredTextParser
                            str={render(sectionContent[17]?.body)}
                            fontSize="18px"
                            fontWeight="400"
                            textAlign={{ base: 'center', md: 'left' }}
                            fontFamily="FedraSansStd-book"
                          />
                          <NextLink
                            href={refineUrlDomain(
                              sectionContent[18]?.linkUrl,
                              region,
                              lang
                            )}
                            passHref
                            legacyBehavior
                          >
                            <Link
                              bg="#E86F34"
                              _hover={{ bg: '#BC570F' }}
                              w={{
                                base: 'auto',
                                sm: 'auto',
                                md: 'auto',
                                lg: lang === 'es' ? '320px' : '298px',
                              }}
                              display="flex"
                              maxW={{
                                base: lang === 'es' ? '300px' : '280px',
                                md: lang === 'es' ? '320px' : '298px',
                              }}
                              // justifyContent="center"
                              // alignItems="center"
                              px="17px"
                              pt={'8px'}
                              color="#fff"
                              borderRadius="full"
                              minH="40px"
                              m={{ base: '30px auto 0', md: '0' }}
                              mt={{ base: '30px', md: '20px' }}
                              mb={{ md: '40px', lg: '10px' }}
                              {...EnrollSectionSt[lang].button}
                            >
                              <LazyLoadImageComponent
                                src="https://static.sadhguru.org/d/46272/1701844040-482ef9a940b329b2555068725d140d40.png"
                                alt=""
                                maxH="52px"
                                maxW="36px"
                                objectFit="cover"
                                w={{ base: '24px', md: '50px' }}
                              />
                              <Box
                                display="flex"
                                mx={{ base: '8px', md: '14px' }}
                                fontSize="18px"
                                mt={{
                                  base: lang === 'es' ? '4px' : '6px',
                                  md: lang === 'es' ? '12px' : '11.5px',
                                }}
                                height="auto"
                              >
                                {sectionContent[18]?.buttonText}
                              </Box>
                            </Link>
                          </NextLink>
                        </>
                      )}
                  </Box>
                </Flex>
              </Flex>
            </Flex>
            <style global>
              {`
              .thanksmessage {            
                font-family: FedraSansStd-book;
                font-size: 18px;
              }
          `}
            </style>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

/**
 *
 * @param {Object} postData
 * @returns
 */
const EnrollSection = ({
  sectionContent,
  region,
  lang,
  prgData,
  couponData,
  pageData,
  country,
  context,
  setContext,
  pageName,
  scrollRef,
}) => {
  // console.log('11EnrollSection', prgData);
  if (pageName === 'yo') {
    return (
      <EnrollContextProvider>
        <EnrollSectionUi
          lang={lang}
          sectionContent={sectionContent}
          region={region}
          pageData={pageData}
          context={context}
          pageName={pageName}
          ref={scrollRef}
        >
          <RegisterForm
            sectionContent={sectionContent}
            EnrollSectionSt={EnrollSectionSt}
            region={region}
            lang={lang}
            prgData={prgData}
            pageData={pageData}
            pageName={pageName}
            context={context}
            setContext={setContext}
          />
        </EnrollSectionUi>
      </EnrollContextProvider>
    );
  } else {
    return (
      <EnrollContextProvider>
        <EnrollSectionUi
          lang={lang}
          sectionContent={sectionContent}
          region={region}
          pageData={pageData}
          context={context}
          pageName={pageName}
          prgData={prgData}
        >
          <RegisterForm
            sectionContent={sectionContent}
            EnrollSectionSt={EnrollSectionSt}
            region={region}
            lang={lang}
            prgData={prgData}
            pageData={pageData}
            pageName={pageName}
            couponData={couponData}
          />
        </EnrollSectionUi>
      </EnrollContextProvider>
    );
  }
};

export default EnrollSection;
